import { format } from "date-fns";
import { generateTimelineDividers } from "./dividers.js";
import { generateTimelineTicks } from "./ticks.js";

function sectionLabelForRangeStart(range, preset) {
  if (preset.dividerUnit === "year") return format(range.start, "yyyy");
  if (preset.dividerUnit === "month") return format(range.start, "MMMM yyyy");
  return format(range.start, "EEE, MMM d");
}

function buildSections(boundaries, range, preset) {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const durationMs = Math.max(1, endMs - startMs);
  const minSectionPct = Math.max(0, Number(preset.minSectionPct) || 1.5);

  const inRange = boundaries.filter((boundary) => boundary.ts > startMs && boundary.ts < endMs);
  const beforeOrAtStart = [...boundaries]
    .reverse()
    .find((boundary) => boundary.ts <= startMs);

  const result = [];
  let sectionStartTs = startMs;
  let currentLabel = beforeOrAtStart?.label || sectionLabelForRangeStart(range, preset);
  let currentStartTs = beforeOrAtStart?.ts || null;

  inRange.forEach((boundary) => {
    result.push({
      startTs: sectionStartTs,
      endTs: boundary.ts,
      label: currentLabel,
      key: `section-${currentStartTs ?? "start"}-${boundary.ts}`,
    });
    sectionStartTs = boundary.ts;
    currentStartTs = boundary.ts;
    currentLabel = boundary.label;
  });

  result.push({
    startTs: sectionStartTs,
    endTs: endMs,
    label: currentLabel,
    key: `section-${currentStartTs ?? "start"}-end`,
  });

  return result.filter(
    (section) => ((section.endTs - section.startTs) / durationMs) * 100 > minSectionPct,
  );
}

function cullTicksByAbsoluteGap(ticks, getAbsolutePx, minGapPx) {
  const safeGapPx = Math.max(1, minGapPx);
  let lastBucket = Number.NEGATIVE_INFINITY;

  return ticks.filter((tick) => {
    const bucket = Math.floor(getAbsolutePx(tick) / safeGapPx);
    if (bucket === lastBucket) return false;
    lastBucket = bucket;
    return true;
  });
}

function cullTicksByScreenGap(ticks, minGapPx) {
  const safeGapPx = Math.max(1, minGapPx);
  let lastShownX = Number.NEGATIVE_INFINITY;

  return ticks.filter((tick) => {
    if (tick.x - lastShownX < safeGapPx) return false;
    lastShownX = tick.x;
    return true;
  });
}

export function buildTimelineAxisModel({
  range,
  preset,
  trackOriginMs,
  msPerPixel,
  viewportWidthPx,
  overscanPx = 220,
}) {
  const getTimestampPx = (ts) => (ts - trackOriginMs) / msPerPixel;

  const boundaries = generateTimelineDividers(range, preset)
    .map((divider) => ({ ...divider, x: getTimestampPx(divider.ts) }))
    .filter(
      (divider) => divider.x > -overscanPx && divider.x < viewportWidthPx + overscanPx,
    )
    .sort((a, b) => a.ts - b.ts);

  const sections = buildSections(boundaries, range, preset).map((section) => {
    const startX = getTimestampPx(section.startTs);
    const endX = getTimestampPx(section.endTs);
    return {
      ...section,
      startX,
      endX,
      widthPx: Math.max(0, endX - startX),
    };
  });

  const { major, minor } = generateTimelineTicks(range, preset);
  const getAbsolutePx = (tick) => tick.ts / msPerPixel;

  const majorAllTicks = major
    .map((tick) => ({ ...tick, x: getTimestampPx(tick.ts) }))
    .sort((a, b) => a.ts - b.ts);
  const majorTicks = majorAllTicks.filter(
    (tick) => tick.x > -overscanPx && tick.x < viewportWidthPx + overscanPx,
  );

  const majorLabelMode = preset.majorLabelMode || "major";
  const majorLabelGapPx = Math.max(
    Number(preset.majorLabelMinGapPx) || 56,
    Math.round(viewportWidthPx * 0.08),
  );
  const boundaryTs = new Set(boundaries.map((item) => item.ts));
  const labelEdgePaddingPx = 14;

  const majorLabels =
    majorLabelMode === "none"
      ? []
      : cullTicksByScreenGap(
          majorAllTicks.filter(
            (tick) =>
              tick.label &&
              tick.x >= labelEdgePaddingPx &&
              tick.x <= viewportWidthPx - labelEdgePaddingPx &&
              !boundaryTs.has(tick.ts),
          ),
          majorLabelGapPx,
        ).map((tick) => ({ ts: tick.ts, x: tick.x, label: tick.label }));

  const minorTickGapPx = Math.max(1, Number(preset.minorTickMinGapPx) || 10);
  const minorAllTicks = minor
    .map((tick) => ({ ...tick, x: getTimestampPx(tick.ts) }))
    .sort((a, b) => a.ts - b.ts);

  const minorTicks = cullTicksByAbsoluteGap(minorAllTicks, getAbsolutePx, minorTickGapPx).filter(
    (tick) => tick.x > -overscanPx && tick.x < viewportWidthPx + overscanPx,
  );

  return {
    sections,
    boundaries,
    majorTicks,
    minorTicks,
    majorLabels,
  };
}
